// Outbound email policy: which environments may send real email.
//
// `prod` always sends and `local` never sends (hardcoded). Every other deployed
// environment is off until switched on at runtime via
// `InternalConfig.features.outboundEmail`, edited on the Internal Features page.
// Dependency-free so both the API (`SendgridService`) and the internal portal
// UI resolve the same answer.

import type {ApiEnvironment} from './constants/environments';

/** Deployed non-prod environments controlled by the runtime toggle. */
export const TOGGLEABLE_EMAIL_ENVIRONMENTS = ['dev', 'staging', 'vbatest'] as const;

export type ToggleableEmailEnvironment = (typeof TOGGLEABLE_EMAIL_ENVIRONMENTS)[number];

/**
 * Keyed by environment name (not a flat flag) so a prod database restored into
 * another environment carries no setting for it and that environment stays off.
 */
export type OutboundEmailConfig = Partial<Record<ToggleableEmailEnvironment, {enabled: boolean}>>;

export type EmailSendDecision =
  | {send: true; reason: 'prod' | 'env_enabled'}
  | {send: false; reason: 'local_guard' | 'env_disabled'};

export function isToggleableEmailEnvironment(
  environment: ApiEnvironment,
): environment is ToggleableEmailEnvironment {
  return (TOGGLEABLE_EMAIL_ENVIRONMENTS as readonly string[]).includes(environment);
}

// Gates the email card by email domain instead of a role.
// TODO: move to a dedicated dev tools surface.
export const OUTBOUND_EMAIL_ADMIN_DOMAINS = [
  'cleverbenefits.com',
  'cleverhealth.ai',
  'cleverrx.com',
] as const;

export function canManageOutboundEmail(email: string | null | undefined): boolean {
  const domain = email?.trim().toLowerCase().split('@')[1];
  return !!domain && (OUTBOUND_EMAIL_ADMIN_DOMAINS as readonly string[]).includes(domain);
}

export function resolveEmailSendDecision(
  environment: ApiEnvironment,
  config: OutboundEmailConfig | null | undefined,
): EmailSendDecision {
  // Local never sends, regardless of any DB setting. To test real delivery from a
  // laptop, comment out this line locally. Never commit that: emailSending.test.ts
  // fails CI if the guard is missing.
  if (environment === 'local') return {send: false, reason: 'local_guard'};
  if (environment === 'prod') return {send: true, reason: 'prod'};
  return config?.[environment]?.enabled === true
    ? {send: true, reason: 'env_enabled'}
    : {send: false, reason: 'env_disabled'};
}
